import { engineComponent } from "neo-common";
import { ComponentTypes } from "../Components/ComponentTypes";
import { Machine } from "../GameComponents/Machine";
import { Settings } from "./Settings";
import { SwitchElement } from "../Components/Elements/Graphics/SwitchElement";
import { Symbols } from "../GameComponents/Symbols";
import { WinAnimator } from "../GameComponents/WinAnimator";
import { ContainerElement } from "../Components/Elements/Graphics/ContainerElement";
import { ReelExtended } from "./ReelExtended";
import { PlayList } from "../Components/Sounds/PlayList";
import { GameData } from "../Channels/Common/Datas/GameData";
import { DataManager } from "../Managers/DataManager";
import { ModuleManager } from "../Managers/ModuleManager";
import { FreeRoundBonus } from "../Channels/Common/Bonuses/FreeRoundBonus";
import { Symbol } from "../GameComponents/Symbol";

@engineComponent(ComponentTypes.DisplayObject)
export class MachineExtended extends Machine {
    public _wasForced: boolean = false;
    private _quickStopDelay: number = 1;
    private teaserIndex: number = 1;

    protected _teaseSwitch: SwitchElement;

    public initialize(
        symbols: Symbols,
        reels: Array<Array<number>>,
        winAnimator: WinAnimator,
        teasing?: ContainerElement,
        teasingSwitch?: SwitchElement,
    ): void {
        super.initialize(symbols, reels, winAnimator, teasing);
        this._teaseSwitch = teasingSwitch;
    }

    protected createReels(reels: Array<Array<number>>) {
        super.createReels(reels);
        this._reels.forEach((reel) => {
            reel.on(ReelExtended.BonusOnReel, this.bonusOnReel.bind(this));
        });
    }

    public spinStart(longRotation?: boolean, useTransform: boolean = false, currentSymbols?: Array<Array<Symbol>>) {
        this._wasForced = false;
        this.teaserIndex = 1;
        super.spinStart(longRotation, useTransform, currentSymbols);
    }

    public spinStop(event: any, force: boolean = false, fromServer: boolean = false, legal: boolean = false): void {
        if (fromServer) {
            this._stoppedFromServer = true;
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            if (this.verifyMinGameRoundDurationConditions()) {
                this._legalStopTimer = setTimeout(
                    () => this.spinStop(null, false, false, true),
                    this.getLegalRemainingSpinDuration(),
                );
            } else {
                this._stoppedFromLegal = true;
            }
        } else if (legal) {
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
        } else {
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
        }

        if (force) {
            PlayList.stop("SND_BaseTeaserLoop", 10);
            if (ModuleManager.freespinsActive()) {
                PlayList.resetVolume("SND_FSLoop", 30);
            } else {
                PlayList.resetVolume("SND_BaseLoop", 30);
            }
            if (this._legalStopTimer != null) {
                clearTimeout(this._legalStopTimer);
            }
            this._stoppedFromLegal = true;
            this._stopTimer.stop();
            this._stoppedFromMachine = true;
            this._wasForced = true;
        }

        if (DEBUG) {
            this.log("spinStop " + this._wasForced);
        }

        if (this._stoppedFromServer && this._stoppedFromMachine && this._stoppedFromLegal) {
            if (
                !(FreeRoundBonus.inFrb && FreeRoundBonus.winPayMethod === "cumulative") &&
                !ModuleManager.freespinsActive()
            ) {
                DataManager.updateDataElement(GameData.DataTotalWin, "0");
                DataManager.updateDataElement(GameData.DataTotalWinDenom, "0");
            }

            if (this._wasForced && this._teasing != null) {
                for (let key in this._teasing.childs) {
                    if (this._teasing.childs[key] instanceof SwitchElement) {
                        this._teasing.childs[key].goToCase("EMPTY");
                    }
                }
            }

            let stopDelay = 0;

            if (this._reels != null) {
                for (let i: number = 0; i < this._reelCount; i++) {
                    if (this._reels[i] != null) {
                        if (this._stoppedSymbolsArray.length !== 0) {
                            this._reels[i].setData(this._stoppedSymbolsArray[i]);

                            let useForceCustomDelay = false;

                            if (this._wasForced) {
                                this._reels[i].spinStop(
                                    this._wasForced,
                                    this._wasForced ? this._quickStopDelay * i : stopDelay,
                                    this._wasForced ? true : useForceCustomDelay,
                                );
                            } else {
                                if (this.checkTeasing(i)) {
                                    stopDelay += 230 + this._stopDelay;
                                    this._reels[i].spinTease([Settings.SymbolTeasing], stopDelay);
                                } else {
                                    stopDelay += this._stopDelay;
                                    this._reels[i].spinStop(false, stopDelay, true);
                                }
                            }
                        } else {
                            if (DEBUG) {
                                this.log("ERROR no stopped symbols");
                            }
                        }
                    } else {
                        if (DEBUG) {
                            this.log("ERROR no reel with index: " + i);
                        }
                    }
                }
            } else {
                if (DEBUG) {
                    this.log("ERROR no reels");
                }
            }
        }
    }

    protected checkTeasing(reelIndex: number): boolean {
        if (ModuleManager.freespinsActive()) {
            ReelExtended.Tease = false;
            return false;
        }
        let previous = 0;
        for (let i = 0; i < reelIndex; i++) {
            for (let j = 0; j <= this._stoppedSymbolsArray[i].symbols.length; j++) {
                if (this._stoppedSymbolsArray[i].symbols[j] === Settings.SymbolTeasing) {
                    previous++;
                }
            }
        }
        if (reelIndex < this._reelCount && previous >= 2 && this._reelCount - reelIndex - 1 + previous >= 2) {
            return true;
        }
        return false;
    }

    protected tease(reelIndex: number) {
        if (ModuleManager.freespinsActive()) {
            ReelExtended.Tease = false;
            return false;
        }
        if (reelIndex == this._reelCount - 1 || this._wasForced) {
            if (this._teaseSwitch != null && this._teaseSwitch.getCurrentCase() !== "EMPTY") {
                this._teaseSwitch.goToCase("STOPPING");
            }
            PlayList.stop("SND_BaseTeaserLoop", 10);
            PlayList.resetVolume("SND_BaseLoop", 30);
            ReelExtended.Tease = false;
        } else if (this.checkTeasing(reelIndex + 1)) {
            if (this._teaseSwitch != null) {
                this._teaseSwitch.goToCase("TEASE_" + (reelIndex + 1));
            }
            PlayList.setVolume("SND_BaseLoop", 0, 60);
            if (!PlayList.isPlaying("SND_BaseTeaserLoop")) {
                PlayList.loop("SND_BaseTeaserLoop");
            }
            PlayList.play("SND_BaseTeaser" + this.teaserIndex);
            this.teaserIndex++;
            ReelExtended.Tease = true;
            ReelExtended.TeaseIndex = reelIndex;
        } else {
            if (this._teaseSwitch != null && this._teaseSwitch.getCurrentCase() !== "EMPTY") {
                this._teaseSwitch.goToCase("STOPPING");
            }
            PlayList.stop("SND_BaseTeaserLoop", 10);
            PlayList.resetVolume("SND_BaseLoop", 30);
            ReelExtended.Tease = false;
        }
    }

    onReelStopped(reelIndex: number) {
        super.onReelStopped(reelIndex);
        if (reelIndex === this._reelCount - 1) {
            PlayList.stop("SND_BaseTeaserLoop", 10);
        }
    }

    private bonusOnReel(reelIndex: number) {
        if (ReelExtended.Tease != true || ReelExtended.TeaseIndex != reelIndex - 1) {
            return;
        }
        if (this._teaseSwitch) {
            this._teaseSwitch.goToCase("BONUS");
        }
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/CustomComponents/MachineExtended.ts
 **/